import React, { useEffect, useState } from "react";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import { useDispatch, useSelector } from "react-redux";
import { setUserInfo } from "../../redux/hejalSlice";
import { Camera, ChevronDown, ChevronUp } from "lucide-react";
import toast from "react-hot-toast";

const Profile = () => {
  const [profile, setProfile] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [imageHover, setImageHover] = useState(false);
  const userInfo = useSelector((state) => state.hejal.userInfo);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [formData, setFormData] = useState({
    societyName: "",
    houseNumber: "",
    street: "",
    city: "",
    country: "",
    state: "",
    pinCode: ""
  });

  // Fetch complete profile data on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoadingProfile(true);
        const response = await ApiService({
          method: "GET",
          endpoint: apiPaths.getProfile, // This will get full profile with cart and wishlist
        });
        
        if (response.success) {
          setProfile(response.data);
          setAddresses(response.data.addresses || []);
          dispatch(setUserInfo(response.data));
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to fetch profile");
        console.error("Error fetching profile:", error);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [dispatch]);

  // Fallback to userInfo if profile is not loaded yet
  useEffect(() => {
    if (userInfo && Object.keys(profile).length === 0) {
      setProfile(userInfo);
    }
  }, [userInfo, profile]);

  const handleAddressInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = ['societyName', 'houseNumber', 'street', 'city', 'country', 'state', 'pinCode'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    // Validate PIN code
    if (!/^\d{6}$/.test(formData.pinCode)) {
      toast.error("PIN code must be exactly 6 digits");
      return;
    }

    try {
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.AddAddress,
        data: formData
      });

      if (response.success) {
        setShowAddressForm(false);
        setFormData({
          societyName: "",
          houseNumber: "",
          street: "",
          city: "",
          country: "",
          state: "",
          pinCode: ""
        });
        setAddresses(prev => [...prev, response.data]);
        toast.success("Address added successfully");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong while adding address");
      console.error("Error adding address:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select a valid image file");
        return;
      }

      setProfile((prev) => ({ ...prev, profileImage: file }));
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      
      // Validate required fields
      if (!profile.fullName?.trim()) {
        toast.error("Full name is required");
        return;
      }

      if (!profile.phoneNumber?.trim()) {
        toast.error("Phone number is required");
        return;
      }

      // Validate phone number format
      if (!/^[6-9]\d{9}$/.test(profile.phoneNumber)) {
        toast.error("Please enter a valid Indian phone number");
        return;
      }

      const formData = new FormData();
      
      // Add text fields
      formData.append('fullName', profile.fullName.trim());
      formData.append('phoneNumber', profile.phoneNumber.trim());
      
      // Add account type if it exists
      if (profile.accountType) {
        formData.append('accountType', profile.accountType);
      }

      // Add image if it's a new file
      if (profile.profileImage instanceof File) {
        formData.append("profileImage", profile.profileImage);
      }

      const response = await ApiService({
        method: "PUT",
        endpoint: apiPaths.UpdateProfile,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.success) {
        setIsEditing(false);
        
        // Update profile with response data
        const updatedProfile = { ...profile, ...response.data };
        setProfile(updatedProfile);
        dispatch(setUserInfo(updatedProfile));

        toast.success('Profile updated successfully');
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error?.response?.data?.message || 'Error while updating profile');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAddress = async (addressId) => {
    try {
      const response = await ApiService({
        method: 'DELETE',
        endpoint: `${apiPaths.deleteAddress}/${addressId}`
      });

      if (response.success) {
        setAddresses(addresses?.filter((add) => add._id !== addressId));
        toast.success("Address deleted successfully");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong while deleting address");
      console.error("Error deleting address:", error);
    }
  };

  const profileFields = [
    { name: "fullName", label: "Full Name", isEditable: true },
    { name: "email", label: "Email", isEditable: false },
    { name: "phoneNumber", label: "Phone Number", isEditable: true }
  ];

  // Show loading spinner while fetching profile
  if (isLoadingProfile) {
    return (
      <div className="profile bg-gradient-to-r from-blue-50 to-indigo-50 min-h-screen py-10 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile bg-gradient-to-r from-blue-50 to-indigo-50 min-h-screen py-10 px-4 transition-all duration-300">
      <div className="max-w-[1200px] mx-auto bg-white shadow-xl rounded-2xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl">
        {/* Header Section with curved design */}
        <div className="bg-[#f8f8f8] h-48 relative">
          {/* Edit Button - Positioned on top right */}
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="absolute top-6 right-6 bg-white px-4 py-2 rounded-full shadow-lg hover:bg-indigo-50 transition-all duration-300 flex items-center space-x-2 font-medium"
          >
            <span>{isEditing ? "Cancel" : "Edit Profile"}</span>
          </button>

          {/* Profile Image - Centered and overlapping the curved section */}
          <div
            className="absolute -bottom-20 left-1/2 transform -translate-x-1/2 cursor-pointer"
            onMouseEnter={() => setImageHover(true)}
            onMouseLeave={() => setImageHover(false)}
          >
            <div className="relative">
              <div className={`h-40 w-40 rounded-full border-4 border-white shadow-lg overflow-hidden transition-all duration-300 ${isEditing ? 'hover:opacity-75' : ''}`}>
                <img
                  className="h-full w-full object-cover"
                  alt="user-img"
                  src={
                    profile.profileImage instanceof File
                      ? URL.createObjectURL(profile.profileImage)
                      : profile.profileImage || "https://img.freepik.com/free-vector/blond-man-smiling_24877-82858.jpg"
                  }
                  onError={(e) => {
                    e.target.src = "https://img.freepik.com/free-vector/blond-man-smiling_24877-82858.jpg";
                  }}
                />
              </div>

              {/* Camera icon overlay for image edit */}
              {isEditing && (
                <label htmlFor="profile-image-upload" className={`absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full cursor-pointer transition-opacity duration-300 ${imageHover ? 'opacity-100' : 'opacity-0'}`}>
                  <Camera size={36} color="white" />
                  <input
                    id="profile-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="pt-24 pb-10 px-6 relative">
          {/* User Name Greeting */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 animate-fadeIn">
              Hey, {profile.fullName?.split(" ")[0] || "User"}
            </h1>
            <p className="text-gray-500 mt-1">Manage your personal information</p>
            
         </div>

          {/* Form Fields in a Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {profileFields.map((field) => (
              <div
                key={field.name}
                className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <label className="block text-gray-600 font-medium mb-2 text-sm">
                  {field.label}
                  {field.isEditable && isEditing && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type="text"
                  name={field.name}
                  value={profile[field.name] || ""}
                  readOnly={!field.isEditable || !isEditing}
                  onChange={handleInputChange}
                  className={`w-full border rounded-lg p-3 focus:outline-none transition-all duration-300 ${!field.isEditable || !isEditing
                    ? "bg-gray-50 text-gray-700"
                    : "border-indigo-200 focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                    }`}
                  placeholder={field.isEditable && isEditing ? `Enter your ${field.label.toLowerCase()}` : ""}
                />
              </div>
            ))}
          </div>

          {/* Save Changes Button */}
          {isEditing && (
            <div className="mt-10 text-center">
              <button
                onClick={handleSave}
                disabled={isLoading}
                className={`bg-secondColor text-white px-8 py-3 rounded-full font-medium shadow-lg hover:shadow-xl transform transition-all duration-300 hover:-translate-y-1 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving Changes
                  </span>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          )}
        </div>

        {/* Address Section - Expandable instead of modal */}
        <div className="px-6 pb-10 flex flex-col items-center">
          <div className="my-6 space-y-4">
            <h2 className="text-lg font-medium text-gray-900">Your Saved Addresses</h2>

            {(!addresses || addresses?.length === 0) ? (
              <p className="text-gray-500 italic">No addresses saved yet</p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
                {addresses.map((add, index) => (
                  <div
                    key={add._id || index}
                    className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="inline-flex items-center justify-center rounded-full bg-blue-100 px-2.5 py-0.5 text-sm font-medium text-blue-800 mb-2">
                          Address {index + 1}
                        </span>
                        <p className="text-gray-900 font-medium">
                          {add.houseNumber}, {add.societyName}, {add.street}, {add.city}, {add.state} - {add.pinCode}, {add.country}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => deleteAddress(add._id)} 
                          className="text-gray-500 hover:text-red-600 p-1" 
                          title="Delete"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <button
            onClick={() => setShowAddressForm(!showAddressForm)}
            className="bg-blue-600 mx-auto text-white px-8 py-3 rounded-full font-medium shadow-lg hover:shadow-xl transform transition-all duration-300 hover:-translate-y-1 flex items-center space-x-2"
          >
            <span>Add Address</span>
            {showAddressForm ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {/* Expandable Address Form */}
          {showAddressForm && (
            <div className="w-full max-w-5xl mt-6 bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden transition-all duration-300 animate-slideDown">
              <div className="p-4 border-b border-gray-100">
                <h3 className="text-lg font-medium text-gray-900">Add New Address</h3>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mx-auto">
                  <div>
                    <label htmlFor="houseNumber" className="block text-sm font-medium text-gray-700">
                      House Number <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="houseNumber" 
                      name="houseNumber" 
                      value={formData.houseNumber} 
                      onChange={handleAddressInputChange} 
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3" 
                      placeholder="Enter house number" 
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="societyName" className="block text-sm font-medium text-gray-700">
                      Society Name <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="societyName" 
                      name="societyName" 
                      value={formData.societyName} 
                      onChange={handleAddressInputChange} 
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3" 
                      placeholder="Enter society name" 
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="street" className="block text-sm font-medium text-gray-700">
                      Street <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="street" 
                      name="street" 
                      value={formData.street} 
                      onChange={handleAddressInputChange} 
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3" 
                      placeholder="Enter street name" 
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                      City <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="city" 
                      name="city" 
                      value={formData.city} 
                      onChange={handleAddressInputChange} 
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3" 
                      placeholder="Enter city name" 
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                      State <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="state" 
                      name="state" 
                      value={formData.state} 
                      onChange={handleAddressInputChange} 
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3" 
                      placeholder="Enter state name" 
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="pinCode" className="block text-sm font-medium text-gray-700">
                      PIN Code <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="pinCode" 
                      name="pinCode" 
                      value={formData.pinCode} 
                      onChange={handleAddressInputChange} 
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3" 
                      placeholder="Enter 6-digit PIN code" 
                      pattern="\d{6}"
                      maxLength="6"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                      Country <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="country" 
                      name="country" 
                      value={formData.country} 
                      onChange={handleAddressInputChange} 
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3" 
                      placeholder="Enter country name" 
                      required
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button 
                    type="button" 
                    onClick={() => setShowAddressForm(false)} 
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Save Address
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); max-height: 0; }
          to { opacity: 1; transform: translateY(0); max-height: 1000px; }
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Profile;